import React from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  FileText,
  TicketPercent,
  Layers,
  Mail,
  LogOut,
  ExternalLink,
  ShoppingBag,
  Database,
  Tags,
  Star,
} from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/orders", icon: ShoppingBag, label: "Orders" },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/categories", icon: Tags, label: "Categories" },
  { to: "/admin/blog", icon: FileText, label: "Blog" },
  { to: "/admin/reviews", icon: Star, label: "Reviews" },
  { to: "/admin/sections", icon: Layers, label: "Sections" },
  { to: "/admin/coupons", icon: TicketPercent, label: "Coupons" },
  { to: "/admin/subscribers", icon: Mail, label: "Subscribers" },
  { to: "/admin/backups", icon: Database, label: "Backups" },
];

export default function AdminLayout() {
  const { user, signOut } = useAdminAuth();
  const navigate = useNavigate();

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0d0d0e] text-[#c3ba9e] flex items-center justify-center">
        <p className="text-sm tracking-[0.32em] uppercase">Authenticating…</p>
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0d0d0e] text-[#e9e6db] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-[#161617] border-r border-[#2a2a2d] flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-7 border-b border-[#2a2a2d]">
          <p className="font-serif-display italic text-3xl text-[#c3ba9e] leading-none">asvasa</p>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8c8a7e] mt-1">Atelier console</p>
        </div>

        <nav className="flex-1 py-6 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-[13px] tracking-wide transition-colors ${
                    isActive
                      ? "bg-[#1f1f21] text-[#c3ba9e] border-l-2 border-[#c3ba9e]"
                      : "text-[#9a9787] hover:text-[#e9e6db] hover:bg-[#1a1a1c] border-l-2 border-transparent"
                  }`
                }
                data-testid={`admin-nav-${n.label.toLowerCase()}`}
              >
                <Icon size={16} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-[#2a2a2d] p-4 space-y-2">
          <button
            onClick={() => window.open("/", "_blank")}
            className="w-full flex items-center gap-3 px-3 py-2 text-[12px] text-[#9a9787] hover:text-[#c3ba9e] hover:bg-[#1f1f21] transition-colors"
          >
            <ExternalLink size={14} /> View site
          </button>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 px-3 py-2 text-[12px] text-[#9a9787] hover:text-[#c3ba9e] hover:bg-[#1f1f21] transition-colors"
            data-testid="admin-sign-out"
          >
            <LogOut size={14} /> Sign out
          </button>
          <p className="text-[10px] text-[#5e5d54] mt-3 px-3">
            Signed in as <span className="text-[#9a9787]">{user.username}</span>
          </p>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden" data-testid="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
